// Laender & Formate — die Regime-Registry als Compliance-Uebersicht (SAP-DRC-Muster
// "Land x Prozess", reduziert). Unverifizierte Regime sind deutlich markiert:
// keine Compliance-Zusage ohne verifizierte Rechtsgrundlage.

import React from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { Card, SectionTitle, Chip } from './ui';

const CoverageMatrix = ({ isDarkMode, t, regimes, formats }) => {
  const textMain = isDarkMode ? 'text-white/80' : 'text-slate-700';
  const textMuted = isDarkMode ? 'text-white/65' : 'text-slate-500';
  const th = `text-left text-[11px] uppercase tracking-wide font-semibold px-3 py-2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`;
  const td = `px-3 py-2.5 text-sm align-top ${textMain}`;
  const fmtName = (id) => (formats || []).find((f) => f.id === id)?.name?.split(' (')[0] || id.toUpperCase();

  return (
    <div className="space-y-6">
      <Card isDarkMode={isDarkMode}>
        <SectionTitle isDarkMode={isDarkMode}>{t('einvoicing.coverageTitle')}</SectionTitle>
        <p className={`text-xs mb-4 ${textMuted}`}>{t('einvoicing.coverageHint')}</p>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className={isDarkMode ? 'border-b border-white/10' : 'border-b border-slate-200'}>
                <th className={th}>{t('einvoicing.covCountry')}</th>
                <th className={th}>{t('einvoicing.covModel')}</th>
                <th className={th}>{t('einvoicing.covFormats')}</th>
                <th className={th}>{t('einvoicing.covChannels')}</th>
                <th className={th}>{t('einvoicing.covSigning')}</th>
                <th className={th}>{t('einvoicing.covArchive')}</th>
                <th className={th}>{t('einvoicing.covStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {(regimes || []).map((r) => (
                <tr key={r.key} className={isDarkMode ? 'border-b border-white/5' : 'border-b border-slate-100'}>
                  <td className={`${td} whitespace-nowrap font-medium`}>{r.flag} {r.country}</td>
                  <td className={td}><span className={`text-xs ${textMuted}`}>{r.model}</span></td>
                  <td className={td}>
                    <span className="flex flex-wrap gap-1">
                      {[...new Set([...r.formats.inbound, ...r.formats.outbound])].map((f) => (
                        <Chip key={f} tone="violet" isDarkMode={isDarkMode}>{fmtName(f)}</Chip>
                      ))}
                    </span>
                  </td>
                  <td className={td}><span className={`text-xs ${textMuted}`}>{r.channels.join(', ')}</span></td>
                  <td className={td}><span className={`text-xs ${textMuted}`}>{t(`einvoicing.signing.${r.signing}`, {}) || r.signing}</span></td>
                  <td className={`${td} tabular-nums`}>{r.archiveYears} {t('einvoicing.years')}</td>
                  <td className={td}>
                    {r.verified
                      ? <span className={`flex items-center gap-1 text-xs ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}><ShieldCheck className="w-3.5 h-3.5" />{t('einvoicing.regimeVerified')}</span>
                      : <span className={`flex items-center gap-1 text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}><AlertTriangle className="w-3.5 h-3.5" />{t('einvoicing.regimeUnverified')}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobil: je Regime eine Karte; Verifiziert-/Unverifiziert-Status oben sichtbar (ohne Scrollen) */}
        <div className="md:hidden space-y-3">
          {(regimes || []).map((r) => (
            <div key={r.key} className={`rounded-xl p-3.5 border ${isDarkMode ? 'border-white/[0.12] bg-white/5' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className={`text-sm font-medium ${textMain}`}>{r.flag} {r.country}</span>
                {r.verified
                  ? <span className={`flex items-center gap-1 text-xs flex-shrink-0 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}><ShieldCheck className="w-3.5 h-3.5" />{t('einvoicing.regimeVerified')}</span>
                  : <span className={`flex items-center gap-1 text-xs flex-shrink-0 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}><AlertTriangle className="w-3.5 h-3.5" />{t('einvoicing.regimeUnverified')}</span>}
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {[...new Set([...r.formats.inbound, ...r.formats.outbound])].map((f) => (
                  <Chip key={f} tone="violet" isDarkMode={isDarkMode}>{fmtName(f)}</Chip>
                ))}
              </div>
              <dl className="space-y-1 text-xs">
                {[
                  [t('einvoicing.covModel'), r.model],
                  [t('einvoicing.covChannels'), r.channels.join(', ')],
                  [t('einvoicing.covSigning'), t(`einvoicing.signing.${r.signing}`, {}) || r.signing],
                  [t('einvoicing.covArchive'), `${r.archiveYears} ${t('einvoicing.years')}`],
                ].map(([label, val]) => (
                  <div key={label} className="flex justify-between gap-3">
                    <dt className={`flex-shrink-0 ${textMuted}`}>{label}</dt>
                    <dd className={`text-right ${textMain}`}>{val}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </Card>

      <Card isDarkMode={isDarkMode}>
        <SectionTitle isDarkMode={isDarkMode}>{t('einvoicing.covMandates')}</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(regimes || []).map((r) => (
            <div key={r.key} className={`rounded-xl p-3 border ${isDarkMode ? 'border-white/[0.12] bg-white/5' : 'border-slate-200 bg-slate-50'}`}>
              <div className={`text-sm font-medium mb-1 ${textMain}`}>{r.flag} {r.country}</div>
              {Object.entries(r.mandate || {}).map(([k, v]) => (
                <div key={k} className={`text-xs ${textMuted}`}>• {v}</div>
              ))}
              <div className={`text-xs mt-1 italic ${textMuted}`}>{r.notes}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default CoverageMatrix;
